import { ObjectFilterAction, type ObjectFilter } from '@talos/kyoko';

const PERMISSION_TO_ACTION = {
  read: ObjectFilterAction.Read,
  write: ObjectFilterAction.Write,
  admin: ObjectFilterAction.Admin,
} as const satisfies Record<string, ObjectFilter['Action']>;

export type ObjectFilterPermissionType = keyof typeof PERMISSION_TO_ACTION;

export const ALL_PERMISSIONS = Object.keys(PERMISSION_TO_ACTION) as ObjectFilterPermissionType[];

export interface ApiKeyAccountPermissions {
  customerAccount: string;
  permissions: ObjectFilterPermissionType[];
}

function toPermission(action: ObjectFilter['Action']): ObjectFilterPermissionType | undefined {
  return (Object.keys(PERMISSION_TO_ACTION) as ObjectFilterPermissionType[]).find(
    permission => PERMISSION_TO_ACTION[permission] === action
  );
}

export function objectFilterKey(filter: ObjectFilter): string {
  return `${filter.Action}|${filter.MarketAccount}`;
}

export function objectFiltersToAccountPermissions(filters: ObjectFilter[] | undefined): ApiKeyAccountPermissions[] {
  const byAccount = new Map<string, ObjectFilterPermissionType[]>();
  for (const filter of filters ?? []) {
    const permission = toPermission(filter.Action);
    if (!permission) {
      continue;
    }
    const existing = byAccount.get(filter.MarketAccount) ?? [];
    if (!existing.includes(permission)) {
      existing.push(permission);
    }
    byAccount.set(filter.MarketAccount, existing);
  }
  return [...byAccount.entries()].map(([customerAccount, permissions]) => ({
    customerAccount,
    permissions,
  }));
}

export function accountPermissionsToObjectFilters(accounts: ApiKeyAccountPermissions[]): ObjectFilter[] {
  return accounts.flatMap(account =>
    account.permissions.map(permission => ({
      Action: PERMISSION_TO_ACTION[permission],
      MarketAccount: account.customerAccount,
    }))
  );
}

export function diffObjectFilters(
  initial: ObjectFilter[] | undefined,
  current: ObjectFilter[] | undefined
): { InsertObjectFilters: ObjectFilter[]; RemoveObjectFilters: ObjectFilter[] } {
  const initialFilters = initial ?? [];
  const currentFilters = current ?? [];
  const initialKeys = new Set(initialFilters.map(objectFilterKey));
  const currentKeys = new Set(currentFilters.map(objectFilterKey));

  return {
    InsertObjectFilters: currentFilters.filter(filter => !initialKeys.has(objectFilterKey(filter))),
    RemoveObjectFilters: initialFilters.filter(filter => !currentKeys.has(objectFilterKey(filter))),
  };
}

export function areObjectFiltersEqual(left: ObjectFilter[] | undefined, right: ObjectFilter[] | undefined): boolean {
  const { InsertObjectFilters, RemoveObjectFilters } = diffObjectFilters(left, right);
  return InsertObjectFilters.length === 0 && RemoveObjectFilters.length === 0;
}
